/**
 * @fileoverview Groups list component for teacher dashboard
 * @description Displays groups in grid or carousel layout with Arc Mosaico and legacy modes
 */

import { ContextType, useContext } from 'react'
import { useCurrentUser } from 'context'
import { useArcTranslation, useFeatureToggle, useMediaQuery } from 'hooks'
import { SimpleGridBreakpoint, useMantineTheme } from '@mantine/core'
import { TeacherDashboardContext } from '../../context'
import {
  Grid, GroupCard, Stack, Heading, EmptyStateBox, Group, Text,
  SimpleGrid, Box, Skeleton, Carousel, carouselDashboard, Button,
} from 'components'
import { GroupsListSkeleton } from '../GroupsListSkeleton'
import { GroupActions } from '../GroupActions'
import { ArcGroupCard } from '../ArcGroupCard'
import { useAddStudentsModal } from '../AddStudentsModal'
import { useTranslation } from 'react-i18next'

import { IconArticleOff, IconUsers, IconUsersGroup, IconUsersPlus } from '@tabler/icons-react'

const CAROUSEL_THRESHOLD = 3
const CARD_HEIGHT = 220

const GRID_BREAKPOINTS: SimpleGridBreakpoint[] = [
  { maxWidth: 'md', cols: 2 },
  { maxWidth: 'sm', cols: 1 },
]

/** @description Renders add students button for mobile header */
const MobileAddStudentsButton = () => {
  const { t } = useTranslation()
  const { open } = useAddStudentsModal()

  return (
    <Button
      variant='filled'
      color='teal'
      radius='xl'
      size='sm'
      leftIcon={<IconUsersPlus size={16} />}
      onClick={() => open()}
    >
      {t('add_students_modal.title')}
    </Button>
  )
}

/** @description Main groups list component with Arc Mosaico and legacy modes */
export const GroupsList = () => {
  const { currentUser } = useCurrentUser()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isArcMosaicoEnabled = useFeatureToggle('arc-mosaico-project') as boolean
  const { groups } = useContext(TeacherDashboardContext)

  if (isArcMosaicoEnabled) {
    return <ArcMosaicoGroupsList groups={groups} isMobile={isMobile} />
  }

  return <LegacyGroupsList groups={groups} currentUser={currentUser} />
}

interface ArcMosaicoGroupsListProps {
  groups: ContextType<typeof TeacherDashboardContext>['groups']
  isMobile: boolean
}

/** @description Arc Mosaico design with carousel support */
const ArcMosaicoGroupsList = ({ groups, isMobile }: ArcMosaicoGroupsListProps) => {
  const { t } = useArcTranslation()
  const isLoading = groups === null
  const hasGroups = groups && groups.length > 0
  const shouldUseCarousel = hasGroups && groups.length > CAROUSEL_THRESHOLD

  const membersLabel = t('new_teacher_dashboard.group_card_students')

  const renderContent = () => {
    if (isLoading) {
      return (
        <SimpleGrid cols={3} spacing='md' breakpoints={GRID_BREAKPOINTS}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height={CARD_HEIGHT} radius='md' animate />
          ))}
        </SimpleGrid>
      )
    }

    if (!hasGroups) {
      return (
        <EmptyStateBox
          withBorder
          text={t('new_teacher_dashboard.no_groups')}
          icon={<IconArticleOff />}
        />
      )
    }

    if (shouldUseCarousel) {
      return (
        <Carousel {...carouselDashboard}>
          {groups.map(group => (
            <Carousel.Slide key={group.id}>
              {/* minHeight (not a fixed height) lets the card grow when text
                  wraps at narrow widths; height 100% keeps slides equalized */}
              <Box sx={{ minHeight: CARD_HEIGHT, height: '100%' }}>
                <ArcGroupCard group={group} membersLabel={membersLabel} />
              </Box>
            </Carousel.Slide>
          ))}
        </Carousel>
      )
    }

    return (
      <SimpleGrid cols={3} spacing='md' breakpoints={GRID_BREAKPOINTS}>
        {groups.map(group => (
          <Box key={group.id} sx={{ minHeight: CARD_HEIGHT }}>
            <ArcGroupCard group={group} membersLabel={membersLabel} />
          </Box>
        ))}
      </SimpleGrid>
    )
  }

  return (
    <Stack spacing='md'>
      <Group position={isMobile ? 'apart' : undefined}>
        <Group spacing='xs'>
          <IconUsersGroup size={24} />
          <Text size='lg' weight={700}>
            {t('new_teacher_dashboard.groups_title')}
          </Text>
        </Group>
        {isMobile && <MobileAddStudentsButton />}
      </Group>
      {renderContent()}
    </Stack>
  )
}

interface LegacyGroupsListProps {
  groups: ContextType<typeof TeacherDashboardContext>['groups']
  currentUser: ReturnType<typeof useCurrentUser>['currentUser']
}

/** @description Legacy design for backwards compatibility */
const LegacyGroupsList = ({ groups, currentUser }: LegacyGroupsListProps) => {
  const { t } = useTranslation()

  const createGroupActions = (groupId: string) => (
    <GroupActions
      groupURL={`/group_school_terms/${groupId}?origin=dashboard`}
      viewDiscURL={`/group_school_terms/${groupId}/group_school_term_mosaics?origin=dashboard`}
      addStudentURL={`/manually_register/${groupId}`}
    />
  )

  if (groups === null) {
    return (
      <Stack spacing='xs'>
        <Heading
          size='md'
          text={t('new_teacher_dashboard.groups_title')}
          icon={<IconUsers size={32} />}
        />
        <GroupsListSkeleton />
      </Stack>
    )
  }

  if (groups.length === 0) {
    return (
      <Stack spacing='xs'>
        <Heading
          size='md'
          text={t('new_teacher_dashboard.groups_title')}
          icon={<IconUsers size={32} />}
        />
        <EmptyStateBox
          withBorder
          text={t('new_teacher_dashboard.no_groups')}
          icon={<IconArticleOff />}
        />
      </Stack>
    )
  }

  return (
    <Stack spacing='xs'>
      <Heading
        size='md'
        text={t('new_teacher_dashboard.groups_title')}
        icon={<IconUsers size={32} />}
      />
      <Grid>
        {groups.map(group => (
          <Grid.Col span={12} md={4} key={group.id}>
            <GroupCard
              group={group}
              membersLabel={t('new_teacher_dashboard.group_card_students')}
              actions={createGroupActions(group.id)}
              showLink={true}
              byCycle={currentUser?.by_cycle || false}
            />
          </Grid.Col>
        ))}
      </Grid>
    </Stack>
  )
}
